import type { ReactNode } from 'react';
import { cn } from '@/lib/utils/cn';

export type StatusVariant =
  | 'success'
  | 'info'
  | 'destructive'
  | 'warning'
  | 'muted';

interface StatusBadgeProps {
  label: string;
  variant?: StatusVariant;
  /** Explicit pill classes (background tint + matching text), overriding
   *  `variant` — e.g. `'bg-[var(--tier-1)]/25 text-[var(--tier-1)]'`. Must be
   *  a full, literal string (Tailwind statically scans source for class
   *  names — composing `bg-x` + `/25`, or deriving `text-x` from `bg-x`, at
   *  runtime never gets generated). Use a real opacity modifier, not a solid
   *  colour-mixed-with-page-background variable — a solid tint only matches
   *  the plain page background and looks off on striped table rows / cards. */
  colorClassName?: string;
  className?: string;
  /** Optional leading icon rendered inside the pill, before the label — e.g. a
   *  padlock marking a locked/read-only state. Decorative: the label still
   *  carries the meaning. */
  icon?: ReactNode;
}

function variantFromLabel(label: string): StatusVariant {
  const l = label.toLowerCase();
  // Neutral "no longer active" states → grey. Checked before the success
  // branch since "inactive" contains "active" (and before destructive since
  // these aren't failures like rejected/terminated).
  if (l.includes('inactiv') || l.includes('offboard')) return 'muted';
  // Denials and negated approvals before the success branch — "approval
  // denied", "not approved" and "disapproved" all contain "approv" and
  // otherwise turn green. ("denied"/"deny", never a bare "deni": this is a
  // textile platform, and "Denim" must not read as a refusal.)
  if (
    l.includes('terminat') ||
    l.includes('reject') ||
    l.includes('blacklist') ||
    l.includes('denied') ||
    l.includes('deny') ||
    l.includes('declin') ||
    l.includes('disapprov') ||
    l.includes('unapprov') ||
    l.includes('not approved')
  )
    return 'destructive';
  // Waiting states before the success branch — "approval pending" contains
  // "approv" but is pending, not approved.
  if (l.includes('review') || l.includes('pending')) return 'warning';
  if (l.includes('approv') || l.includes('active') || l.includes('complet'))
    return 'success';
  // Teal (info) — in-progress reads as the brand's active/ongoing hue (matches
  // the progress-bar teal), alongside onboarding.
  if (l.includes('onboard') || l.includes('progress')) return 'info';
  return 'muted';
}

// Soft tint background + a darker, saturated same-hue text for a legible
// contrast (the old `bg-x/25 text-x` used the same medium shade for both, so
// text barely separated from the fill). `info` is teal — the brand hue — not
// the stray blue that appeared nowhere else in the palette.
const VARIANT_CLASS: Record<StatusVariant, string> = {
  success:
    'bg-green-500/15 text-green-700 dark:bg-green-500/25 dark:text-green-300',
  info: 'bg-teal-500/15 text-teal-700 dark:bg-teal-500/25 dark:text-teal-300',
  destructive:
    'bg-rose-500/15 text-rose-700 dark:bg-rose-500/25 dark:text-rose-300',
  warning:
    'bg-amber-500/20 text-amber-800 dark:bg-amber-500/25 dark:text-amber-300',
  muted: 'bg-zinc-500/15 text-zinc-700 dark:bg-zinc-400/15 dark:text-zinc-300',
};

/** Coloured pill — background tint + matching text colour convey status,
 *  no dot indicator. */
export function StatusBadge({
  label,
  variant,
  colorClassName,
  className,
  icon,
}: StatusBadgeProps) {
  const v = variant ?? variantFromLabel(label);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium',
        colorClassName ?? VARIANT_CLASS[v],
        className,
      )}
    >
      {icon}
      {label}
    </span>
  );
}
